import React, { useState, useEffect } from 'react';
import './App.css';

const FIREWORKS_COLORS = ['#FF3333', '#FFD700', '#33CCFF', '#FF33CC', '#66FF33', '#FF6600', '#CC33FF', '#00FFCC'];

const Fireworks = () => (
  <div className="fireworks">
    {FIREWORKS_COLORS.map((color, i) => (
      <span
        key={i}
        className="fw-particle"
        style={{ '--angle': `${i * 45}deg`, '--delay': `${i * 0.175}s`, background: color }}
      />
    ))}
  </div>
);

function App() {
  // Champs principaux
  const [purchasePrice, setPurchasePrice] = useState('');
  const [sellingPriceVinted_TTC, setSellingPriceVinted_TTC] = useState('');
  const [sellingPriceFBA_TTC, setSellingPriceFBA_TTC] = useState('');

  // Gestion des devises
  const [currency, setCurrency] = useState('EUR');
  const defaultRates = { EUR: 1, PLN: 4.40, SEK: 10.98, GBP: 0.83, USD: 1.08 };
  const savedRates = (() => { try { return JSON.parse(localStorage.getItem('exchangeRates') || 'null'); } catch { return null; } })();
  const [exchangeRates, setExchangeRates] = useState(savedRates || defaultRates);
  const [ratesLoading, setRatesLoading] = useState(false);

  // Frais de base
  const [amazonCommission, setAmazonCommission] = useState({ value: 15, type: '%' });
  const [fbaFees, setFbaFees] = useState(2.26);
  const [vintedPackaging, setVintedPackaging] = useState(0.50);
  const [digitalServicesFee, setDigitalServicesFee] = useState(3);
  const [tvaRate, setTvaRate] = useState(20);

  // Frais supplémentaires
  const [additionalFees, setAdditionalFees] = useState([]);

  // Sections repliables
  const [showBaseFees, setShowBaseFees] = useState(false);
  const [showAdditionalFees, setShowAdditionalFees] = useState(false);
  const [showVintedDetails, setShowVintedDetails] = useState(false);
  const [showFBADetails, setShowFBADetails] = useState(false);

  const getCurrencySymbol = (curr) => {
    const symbols = { EUR: '€', PLN: 'zł', SEK: 'kr', GBP: '£', USD: '$' };
    return symbols[curr] || '€';
  };

  const getMarginColor = (marginPercent) => {
    if (marginPercent < 8) return '#DC2626';
    if (marginPercent < 15) return '#F97316';
    return '#059669';
  };

  const getRoiColor = (roiPercent) => {
    if (roiPercent < 0) return '#DC2626';
    if (roiPercent < 100) return '#F97316';
    return '#059669';
  };

  const getMultiplierColor = (multiplier) => {
    if (multiplier < 1) return '#DC2626';
    if (multiplier < 2) return '#F97316';
    return '#059669';
  };

  // Récupération des taux de change depuis l'API BCE
  useEffect(() => {
    const fetchExchangeRates = async () => {
      setRatesLoading(true);
      try {
        const response = await fetch('https://api.frankfurter.app/latest?from=EUR&to=PLN,SEK,GBP,USD');
        if (response.ok) {
          const data = await response.json();
          const freshRates = {
            EUR: 1,
            PLN: data.rates.PLN || 4.40,
            SEK: data.rates.SEK || 10.98,
            GBP: data.rates.GBP || 0.83,
            USD: data.rates.USD || 1.08
          };
          setExchangeRates(freshRates);
          localStorage.setItem('exchangeRates', JSON.stringify(freshRates));
        } else {
          console.warn('Échec de récupération des taux, utilisation des valeurs par défaut');
        }
      } catch (error) {
        console.warn('Erreur lors de la récupération des taux de change:', error);
      } finally {
        setRatesLoading(false);
      }
    };
    fetchExchangeRates();
  }, []);

  // Charger depuis localStorage au démarrage
  useEffect(() => {
    const savedData = localStorage.getItem('calculatorData');
    if (savedData) {
      try {
        const data = JSON.parse(savedData);
        if (data.amazonCommission !== undefined) {
          if (typeof data.amazonCommission === 'number') {
            setAmazonCommission({ value: data.amazonCommission, type: '%' });
          } else {
            setAmazonCommission(data.amazonCommission);
          }
        }
        if (data.fbaFees !== undefined) setFbaFees(data.fbaFees);
        if (data.vintedPackaging !== undefined) setVintedPackaging(data.vintedPackaging);
        if (data.digitalServicesFee !== undefined) setDigitalServicesFee(data.digitalServicesFee);
        if (data.tvaRate !== undefined) setTvaRate(data.tvaRate);
        if (data.additionalFees) setAdditionalFees(data.additionalFees);
        if (data.currency) setCurrency(data.currency);
      } catch (e) {
        console.error('Erreur lors du chargement des données:', e);
      }
    }
  }, []);

  // Sauvegarder dans localStorage à chaque changement
  useEffect(() => {
    const dataToSave = {
      amazonCommission,
      fbaFees,
      vintedPackaging,
      digitalServicesFee,
      tvaRate,
      additionalFees,
      currency
    };
    localStorage.setItem('calculatorData', JSON.stringify(dataToSave));
  }, [amazonCommission, fbaFees, vintedPackaging, digitalServicesFee, tvaRate, additionalFees, currency]);

  const handleNumberInput = (value, setter) => {
    const num = parseFloat(value);
    if (value === '' || (!isNaN(num) && num >= 0)) {
      setter(value);
    }
  };

  // Calcul des frais supplémentaires (normalisés en EUR)
  const calculateAdditionalFees = (priceTTC_EUR, type) => {
    const result = additionalFees.reduce((total, fee) => {
      const amountLocal = type === 'vinted' ? parseFloat(fee.vintedAmount) || 0 : parseFloat(fee.fbaAmount) || 0;
      if (fee.unit === '€') {
        return total + (amountLocal / exchangeRates[currency]);
      } else {
        return total + (priceTTC_EUR * amountLocal / 100);
      }
    }, 0);
    return Number(result) || 0;
  };

  // Calculs Vinted Pro (tous les calculs en EUR)
  const calculateVinted = () => {
    const purchase = parseFloat(purchasePrice) || 0;
    const priceTTC_Local = parseFloat(sellingPriceVinted_TTC) || 0;
    const priceTTC = priceTTC_Local / exchangeRates[currency];
    const packagingLocal = parseFloat(vintedPackaging) || 0;
    const packagingAmount = packagingLocal / exchangeRates[currency];
    const tva = currency === 'USD' ? 0 : (parseFloat(tvaRate) || 0);

    if (priceTTC === 0) {
      return {
        priceHT: 0,
        tvaAmount: 0,
        packaging: packagingAmount,
        additionalFees: 0,
        totalCosts: 0,
        margin: 0,
        marginPercent: 0
      };
    }

    const priceHT = priceTTC / (1 + tva / 100);
    const tvaAmount = priceTTC - priceHT;
    const additionalFeesAmount = calculateAdditionalFees(priceTTC, 'vinted');
    const totalCosts = purchase + packagingAmount + additionalFeesAmount + tvaAmount;
    const margin = priceTTC - totalCosts;
    const marginPercent = priceHT > 0 ? (margin / priceHT * 100) : 0;

    return {
      priceHT,
      tvaAmount,
      packaging: packagingAmount,
      additionalFees: additionalFeesAmount,
      totalCosts,
      margin,
      marginPercent
    };
  };

  // Calculs FBA (tous les calculs en EUR) — inchangé
  const calculateFBA = () => {
    const purchase = parseFloat(purchasePrice) || 0;
    const priceTTC_Local = parseFloat(sellingPriceFBA_TTC) || 0;
    const priceTTC = priceTTC_Local / exchangeRates[currency];
    const fbaFeesLocal = parseFloat(fbaFees) || 0;
    const fbaFeesAmount = fbaFeesLocal / exchangeRates[currency];
    const digitalRate = parseFloat(digitalServicesFee) || 0;
    const tva = currency === 'USD' ? 0 : (parseFloat(tvaRate) || 0);

    if (priceTTC === 0) {
      return {
        priceHT: 0,
        tvaAmount: 0,
        commission: 0,
        fbaFee: fbaFeesAmount,
        digitalFee: 0,
        additionalFees: 0,
        totalCosts: 0,
        margin: 0,
        marginPercent: 0
      };
    }

    const priceHT = priceTTC / (1 + tva / 100);
    const tvaAmount = priceTTC - priceHT;

    let commission;
    if (amazonCommission.type === '%') {
      const commissionRate = parseFloat(amazonCommission.value) || 0;
      commission = priceTTC * commissionRate / 100;
    } else {
      const commissionLocal = parseFloat(amazonCommission.value) || 0;
      commission = commissionLocal / exchangeRates[currency];
    }

    const rate = exchangeRates[currency];
    const commissionRounded = Math.round(commission * rate * 100) / 100;
    const fbaRounded = Math.round(fbaFeesLocal * 100) / 100;
    const digitalFee_local = Math.round(commissionRounded * digitalRate) / 100 + Math.round(fbaRounded * digitalRate) / 100;
    const digitalFee = digitalFee_local / rate;
    const additionalFeesAmount = calculateAdditionalFees(priceTTC, 'fba');
    const totalCosts = purchase + commission + fbaFeesAmount + digitalFee + additionalFeesAmount + tvaAmount;
    const margin = priceTTC - totalCosts;
    const marginPercent = priceHT > 0 ? (margin / priceHT * 100) : 0;

    return {
      priceHT,
      tvaAmount,
      commission,
      fbaFee: fbaFeesAmount,
      digitalFee,
      additionalFees: additionalFeesAmount,
      totalCosts,
      margin,
      marginPercent
    };
  };

  const vintedResults = calculateVinted();
  const fbaResults = calculateFBA();
  const difference = fbaResults.margin - vintedResults.margin;
  const vintedActive = parseFloat(sellingPriceVinted_TTC) > 0;
  const fbaActive = parseFloat(sellingPriceFBA_TTC) > 0;

  // Gestion des frais supplémentaires
  const addAdditionalFee = () => {
    setAdditionalFees([
      ...additionalFees,
      { id: Date.now(), name: '', vintedAmount: '0', fbaAmount: '0', unit: '€' }
    ]);
  };

  const removeAdditionalFee = (id) => {
    setAdditionalFees(additionalFees.filter(fee => fee.id !== id));
  };

  const updateAdditionalFee = (id, field, value) => {
    setAdditionalFees(additionalFees.map(fee =>
      fee.id === id ? { ...fee, [field]: value } : fee
    ));
  };

  // Détails Vinted Pro
  const getVintedDetails = () => {
    const purchase = parseFloat(purchasePrice) || 0;
    const tva = currency === 'USD' ? 0 : (parseFloat(tvaRate) || 0);
    const totalSorties = Number(vintedResults.totalCosts) || 0;
    const rate = exchangeRates[currency];

    return [
      { label: currency === 'USD' ? 'Prix HT' : 'Prix TTC', value: Number(vintedResults.priceHT * (1 + tva / 100)) || 0, inLocal: true },
      ...(currency !== 'USD' ? [{ label: 'Prix HT', value: Number(vintedResults.priceHT) || 0, inLocal: true }] : []),
      ...(currency !== 'USD' ? [{ label: `TVA à reverser (${tva}%)`, value: Number(vintedResults.tvaAmount) || 0, inLocal: true }] : []),
      { label: "Frais d'emballage", value: Number(vintedResults.packaging) || 0, inLocal: true },
      ...additionalFees.map(fee => {
        const amountLocal = parseFloat(fee.vintedAmount) || 0;
        const priceTTC_Local = parseFloat(sellingPriceVinted_TTC) || 0;
        const priceTTC_EUR = priceTTC_Local / rate;
        const calculatedValue = fee.unit === '€'
          ? amountLocal / rate
          : priceTTC_EUR * amountLocal / 100;
        return {
          label: fee.name || 'Frais sans nom',
          value: Number(calculatedValue) || 0,
          inLocal: true
        };
      }),
      { label: "Prix d'achat", value: Number(purchase) || 0, inLocal: false },
      { label: 'TOTAL DES SORTIES', value: totalSorties, isFinal: true, inLocal: true },
      { label: 'MARGE FINALE', value: Number(vintedResults.margin) || 0, isFinal: true, inLocal: true }
    ];
  };

  // Détails FBA
  const getFBADetails = () => {
    const purchase = parseFloat(purchasePrice) || 0;
    const tva = currency === 'USD' ? 0 : (parseFloat(tvaRate) || 0);
    const totalSorties = Number(fbaResults.totalCosts) || 0;
    const rate = exchangeRates[currency];

    return [
      { label: currency === 'USD' ? 'Prix HT' : 'Prix TTC', value: Number(fbaResults.priceHT * (1 + tva / 100)) || 0, inLocal: true },
      ...(currency !== 'USD' ? [{ label: 'Prix HT', value: Number(fbaResults.priceHT) || 0, inLocal: true }] : []),
      ...(currency !== 'USD' ? [{ label: `TVA à reverser (${tva}%)`, value: Number(fbaResults.tvaAmount) || 0, inLocal: true }] : []),
      { label: 'Commission Amazon', value: Number(fbaResults.commission) || 0, inLocal: true },
      { label: 'Frais FBA', value: Number(fbaResults.fbaFee) || 0, inLocal: true },
      { label: 'Service numérique', value: Number(fbaResults.digitalFee) || 0, inLocal: true },
      ...additionalFees.map(fee => {
        const amountLocal = parseFloat(fee.fbaAmount) || 0;
        const priceTTC_Local = parseFloat(sellingPriceFBA_TTC) || 0;
        const priceTTC_EUR = priceTTC_Local / rate;
        const calculatedValue = fee.unit === '€'
          ? amountLocal / rate
          : priceTTC_EUR * amountLocal / 100;
        return {
          label: fee.name || 'Frais sans nom',
          value: Number(calculatedValue) || 0,
          inLocal: true
        };
      }),
      { label: "Prix d'achat", value: Number(purchase) || 0, inLocal: false },
      { label: 'TOTAL DES SORTIES', value: totalSorties, isFinal: true, inLocal: true },
      { label: 'MARGE FINALE', value: Number(fbaResults.margin) || 0, isFinal: true, inLocal: true }
    ];
  };

  return (
    <div className="App">
      <header className="header">
        <h1>Calculatrice de Marge</h1>
        <p className="subtitle">Clem Provence</p>
      </header>

      <div className="tabs-nav">
        <button className="tab-btn tab-btn-active">
          FBA / Vinted
        </button>
      </div>

      <div className="container">
        {/* Calculatrice principale */}
        <div className="card main-calculator">
          <h2>Calculatrice</h2>

          <div className="input-group">
            <label>Pays / Devise {ratesLoading && '(Chargement des taux...)'}</label>
            <select
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              className="currency-select"
            >
              <option value="EUR">🇫🇷 France (EUR - €)</option>
              <option value="GBP">🇬🇧 Royaume-Uni (GBP - £)</option>
              <option value="USD">🇺🇸 États-Unis (USD - $)</option>
              <option value="PLN">🇵🇱 Pologne (PLN - zł)</option>
              <option value="SEK">🇸🇪 Suède (SEK - kr)</option>
            </select>
            {currency !== 'EUR' && (
              <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '4px' }}>
                Taux: 1 € = {exchangeRates[currency].toFixed(4)} {getCurrencySymbol(currency)}
              </div>
            )}
          </div>

          <div className="input-group">
            <label>Prix d'achat (€)</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={purchasePrice}
              onChange={(e) => handleNumberInput(e.target.value, setPurchasePrice)}
              placeholder="0.00"
            />
          </div>

          <div className="input-group">
            <label>Prix de vente Vinted {currency === 'USD' ? 'HT' : 'TTC'} ({getCurrencySymbol(currency)})</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={sellingPriceVinted_TTC}
              onChange={(e) => handleNumberInput(e.target.value, setSellingPriceVinted_TTC)}
              placeholder="0.00"
            />
          </div>

          <div className="input-group">
            <label>Prix de vente FBA {currency === 'USD' ? 'HT' : 'TTC'} ({getCurrencySymbol(currency)})</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={sellingPriceFBA_TTC}
              onChange={(e) => handleNumberInput(e.target.value, setSellingPriceFBA_TTC)}
              placeholder="0.00"
            />
          </div>
        </div>

        {/* Résultats */}
        <div className="card results-card">
          <h2>Résultats (calculés en €)</h2>

          <div className="results-grid">
            <div className="result-item">
              <h3>Vinted Pro</h3>
              {vintedActive ? (
                <>
                  <div className="result-value" style={{ color: getMarginColor(vintedResults.marginPercent) }}>
                    {vintedResults.margin.toFixed(2)} €
                  </div>
                  {currency !== 'EUR' && (
                    <div className="result-local">
                      ≈ {(vintedResults.margin * exchangeRates[currency]).toFixed(2)} {getCurrencySymbol(currency)}
                    </div>
                  )}
                  <div className="result-percent">
                    ({vintedResults.marginPercent.toFixed(2)}%)
                  </div>
                  {currency !== 'USD' && (
                    <div className="result-ht">
                      Prix HT: {vintedResults.priceHT.toFixed(2)} €
                    </div>
                  )}
                  {currency !== 'EUR' && currency !== 'USD' && (
                    <div className="result-ht">
                      ≈ {(vintedResults.priceHT * exchangeRates[currency]).toFixed(2)} {getCurrencySymbol(currency)}
                    </div>
                  )}
                </>
              ) : (
                <div className="result-value result-dash">—</div>
              )}
            </div>

            <div className="result-item">
              <h3>FBA</h3>
              {fbaActive ? (
                <>
                  <div className="result-value" style={{ color: getMarginColor(fbaResults.marginPercent) }}>
                    {fbaResults.margin.toFixed(2)} €
                  </div>
                  {currency !== 'EUR' && (
                    <div className="result-local">
                      ≈ {(fbaResults.margin * exchangeRates[currency]).toFixed(2)} {getCurrencySymbol(currency)}
                    </div>
                  )}
                  <div className="result-percent">
                    ({fbaResults.marginPercent.toFixed(2)}%)
                  </div>
                  {currency !== 'USD' && (
                    <div className="result-ht">
                      Prix HT: {fbaResults.priceHT.toFixed(2)} €
                    </div>
                  )}
                  {currency !== 'EUR' && currency !== 'USD' && (
                    <div className="result-ht">
                      ≈ {(fbaResults.priceHT * exchangeRates[currency]).toFixed(2)} {getCurrencySymbol(currency)}
                    </div>
                  )}
                </>
              ) : (
                <div className="result-value result-dash">—</div>
              )}
            </div>

            <div className="result-item">
              <h3>ROI</h3>
              {parseFloat(purchasePrice) > 0 ? (
                <>
                  <div className="roi-section">
                    <div className="roi-label">Vinted Pro</div>
                    {vintedActive ? (
                      <>
                        <div className="roi-percent" style={{ color: getRoiColor(vintedResults.margin / parseFloat(purchasePrice) * 100) }}>
                          {(vintedResults.margin / parseFloat(purchasePrice) * 100).toFixed(1)}%
                        </div>
                        <div className="roi-multiplier-wrapper">
                          <div className="roi-multiplier" style={{ color: getMultiplierColor((parseFloat(purchasePrice) + vintedResults.margin) / parseFloat(purchasePrice)) }}>
                            x{((parseFloat(purchasePrice) + vintedResults.margin) / parseFloat(purchasePrice)).toFixed(2)}
                          </div>
                          {(parseFloat(purchasePrice) + vintedResults.margin) / parseFloat(purchasePrice) >= 3 && <Fireworks />}
                        </div>
                      </>
                    ) : (
                      <div className="roi-percent result-dash">—</div>
                    )}
                  </div>
                  <div className="roi-section">
                    <div className="roi-label">FBA</div>
                    {fbaActive ? (
                      <>
                        <div className="roi-percent" style={{ color: getRoiColor(fbaResults.margin / parseFloat(purchasePrice) * 100) }}>
                          {(fbaResults.margin / parseFloat(purchasePrice) * 100).toFixed(1)}%
                        </div>
                        <div className="roi-multiplier-wrapper">
                          <div className="roi-multiplier" style={{ color: getMultiplierColor((parseFloat(purchasePrice) + fbaResults.margin) / parseFloat(purchasePrice)) }}>
                            x{((parseFloat(purchasePrice) + fbaResults.margin) / parseFloat(purchasePrice)).toFixed(2)}
                          </div>
                          {(parseFloat(purchasePrice) + fbaResults.margin) / parseFloat(purchasePrice) >= 3 && <Fireworks />}
                        </div>
                      </>
                    ) : (
                      <div className="roi-percent result-dash">—</div>
                    )}
                  </div>
                </>
              ) : (
                <div className="result-info">Entrez un prix d'achat</div>
              )}
            </div>
          </div>
        </div>

        {/* Frais de base */}
        <div className="card collapsible-card">
          <div
            className="collapsible-header"
            onClick={() => setShowBaseFees(!showBaseFees)}
          >
            <h2>Frais de base</h2>
            <span className="toggle-icon">{showBaseFees ? '▼' : '▶'}</span>
          </div>

          {showBaseFees && (
            <div className="collapsible-content">
              <div className="input-group">
                <label>Commission Amazon (FBA)</label>
                <div className="commission-hybrid-input">
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={amazonCommission.value}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === '' || (!isNaN(parseFloat(val)) && parseFloat(val) >= 0)) {
                        setAmazonCommission({ ...amazonCommission, value: val });
                      }
                    }}
                    className="commission-value-input"
                  />
                  <select
                    value={amazonCommission.type}
                    onChange={(e) => setAmazonCommission({ ...amazonCommission, type: e.target.value })}
                    className="commission-type-select"
                  >
                    <option value="%">%</option>
                    <option value={currency}>{getCurrencySymbol(currency)}</option>
                  </select>
                </div>
              </div>

              <div className="input-group">
                <label>Frais FBA ({getCurrencySymbol(currency)})</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={fbaFees}
                  onChange={(e) => handleNumberInput(e.target.value, setFbaFees)}
                />
              </div>

              <div className="input-group">
                <label>Frais d'emballage Vinted ({getCurrencySymbol(currency)})</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={vintedPackaging}
                  onChange={(e) => handleNumberInput(e.target.value, setVintedPackaging)}
                />
              </div>

              <div className="input-group">
                <label>Frais service numérique FBA (%)</label>
                <input
                  type="number"
                  min="0"
                  step="0.1"
                  value={digitalServicesFee}
                  onChange={(e) => handleNumberInput(e.target.value, setDigitalServicesFee)}
                />
              </div>

              {currency !== 'USD' && (
                <div className="input-group">
                  <label>TVA (%)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.1"
                    value={tvaRate}
                    onChange={(e) => handleNumberInput(e.target.value, setTvaRate)}
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Frais supplémentaires */}
        <div className="card collapsible-card">
          <div
            className="collapsible-header"
            onClick={() => setShowAdditionalFees(!showAdditionalFees)}
          >
            <h2>Frais supplémentaires</h2>
            <span className="toggle-icon">{showAdditionalFees ? '▼' : '▶'}</span>
          </div>

          {showAdditionalFees && (
            <div className="collapsible-content">
              {additionalFees.map((fee) => (
                <div key={fee.id} className="additional-fee-item">
                  <input
                    type="text"
                    placeholder="Nom du frais"
                    value={fee.name}
                    onChange={(e) => updateAdditionalFee(fee.id, 'name', e.target.value)}
                    className="fee-name-input"
                  />

                  <div className="fee-amounts">
                    <div className="fee-amount-group">
                      <label>Vinted</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={fee.vintedAmount}
                        onChange={(e) => updateAdditionalFee(fee.id, 'vintedAmount', e.target.value)}
                      />
                    </div>

                    <div className="fee-amount-group">
                      <label>FBA</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={fee.fbaAmount}
                        onChange={(e) => updateAdditionalFee(fee.id, 'fbaAmount', e.target.value)}
                      />
                    </div>

                    <select
                      value={fee.unit}
                      onChange={(e) => updateAdditionalFee(fee.id, 'unit', e.target.value)}
                      className="fee-unit-select"
                    >
                      <option value="€">{getCurrencySymbol(currency)}</option>
                      <option value="%">%</option>
                    </select>

                    <button
                      onClick={() => removeAdditionalFee(fee.id)}
                      className="remove-fee-btn"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              ))}

              <button onClick={addAdditionalFee} className="add-fee-btn">
                + Ajouter un frais
              </button>
            </div>
          )}
        </div>

        {/* Détails Vinted Pro */}
        <div className="card collapsible-card">
          <div
            className="collapsible-header"
            onClick={() => setShowVintedDetails(!showVintedDetails)}
          >
            <h2>Détails des calculs Vinted Pro</h2>
            <span className="toggle-icon">{showVintedDetails ? '▼' : '▶'}</span>
          </div>

          {showVintedDetails && (
            <div className="collapsible-content details-content">
              {getVintedDetails().map((item, index) => (
                <div
                  key={index}
                  className={`detail-row ${item.isFinal ? 'final-row' : ''}`}
                >
                  <span className="detail-label">{item.label}</span>
                  <span className="detail-value">
                    {item.value.toFixed(2)} €
                    {item.inLocal && currency !== 'EUR' && (
                      <span className="detail-local">
                        {' '}(≈ {(item.value * exchangeRates[currency]).toFixed(2)} {getCurrencySymbol(currency)})
                      </span>
                    )}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Détails FBA — inchangé */}
        <div className="card collapsible-card">
          <div
            className="collapsible-header"
            onClick={() => setShowFBADetails(!showFBADetails)}
          >
            <h2>Détails des calculs FBA</h2>
            <span className="toggle-icon">{showFBADetails ? '▼' : '▶'}</span>
          </div>

          {showFBADetails && (
            <div className="collapsible-content details-content">
              {getFBADetails().map((item, index) => (
                <div
                  key={index}
                  className={`detail-row ${item.isFinal ? 'final-row' : ''}`}
                >
                  <span className="detail-label">{item.label}</span>
                  <span className="detail-value">
                    {item.value.toFixed(2)} €
                    {item.inLocal && currency !== 'EUR' && (
                      <span className="detail-local">
                        {' '}(≈ {(item.value * exchangeRates[currency]).toFixed(2)} {getCurrencySymbol(currency)})
                      </span>
                    )}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default App;
